import React, { useEffect, useMemo, useRef, useState } from "react";
import ClientDocuments from "../components/client-table/ClientDocuments";
import DocumentsPageHeader from "../components/common/DocumentsPageHeader";
import Loading from "../components/material-ui/Loading";
import { AuthContext } from "../context/Auth/AuthContext";
import { RolesContext } from "../context/RolesContext";
import { createEmbedSession } from "../api/embed";
import "../styles/Clients.css";
import "../styles/Documents.css";

// Public /embed page (no DMS login, no sidebar) for iframes / links in an
// external system. The URL carries a short-lived, one-time launch token that
// the external system's SERVER signed with the secret from Administration >
// Embed & API. It is exchanged for a short-lived DMS session for the configured
// integration user, then the existing Clients page documents view
// (Requested / Uploaded, ClientDocuments) is rendered unchanged, so every
// action goes through the existing DMS APIs and shows up in the DMS.
//
// The session lives in memory only: it is provided through local AuthContext /
// RolesContext providers and never written to localStorage, so it can't
// replace the DMS login of someone who also uses the DMS in this browser.

const readLaunchParams = () => {
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  return {
    launchToken: hash.get("token") || query.get("token") || "",
    projectKey: query.get("project_key") || hash.get("project_key") || "",
  };
};

const noop = () => {};

const EmbedMessage = ({ title, message }) => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
    <div className="max-w-md rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
      <h1 className="mb-2 text-lg font-semibold text-nbl-primary">{title}</h1>
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  </div>
);

const EmbedClientDocuments = () => {
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    // StrictMode runs effects twice in development; the launch token is
    // single-use, so exchange it only once.
    if (startedRef.current) return;
    startedRef.current = true;

    const { launchToken, projectKey } = readLaunchParams();
    // Remove the token from the address bar / history right away.
    window.history.replaceState(null, "", window.location.pathname);

    if (!launchToken) {
      setError("This page must be opened from an embed link that includes a token.");
      return;
    }

    createEmbedSession({ launchToken, projectKey }).then((res) => {
      if (res?.code === 200 && res.data?.token) {
        setSession(res.data);
      } else {
        setError(res?.message || "Unable to open the client documents.");
      }
    });
  }, []);

  // Tell a same-origin parent (the Embed & API Live Preview) that opening has
  // finished, so it can hide its loading overlay. Other origins never get it.
  useEffect(() => {
    if ((session || error) && window.parent !== window) {
      window.parent.postMessage({ type: "dms-embed-ready" }, window.location.origin);
    }
  }, [session, error]);

  useEffect(() => {
    if (!session?.expires_in) return;
    const timer = setTimeout(() => setExpired(true), session.expires_in * 1000);
    return () => clearTimeout(timer);
  }, [session]);

  const authValue = useMemo(
    () =>
      session && {
        token: session.token,
        id: session.user.id,
        user_id: session.user.user_id,
        firstName: session.user.first_name,
        lastName: session.user.last_name,
        phoneNumber: null,
        is_admin: session.user.is_admin,
        email_id: session.user.email,
        dispatch: noop,
      },
    [session],
  );

  const rolesValue = useMemo(
    () =>
      session && {
        roles: null,
        setRoles: noop,
        getPermissions: noop,
        permissions: session.permissions || [],
      },
    [session],
  );

  if (error) return <EmbedMessage title="Cannot open documents" message={error} />;
  if (expired)
    return (
      <EmbedMessage
        title="Session expired"
        message="Reload the page in your application to continue."
      />
    );
  if (!session) return <Loading />;

  const client = session.client;

  return (
    <AuthContext.Provider value={authValue}>
      <RolesContext.Provider value={rolesValue}>
        {/* body has overflow:hidden app-wide, so this page scrolls itself. */}
        <div className="document-content dms-embed h-screen overflow-y-auto">
          <DocumentsPageHeader
            showHeader={false}
            breadcrumbs={[
              { label: "Clients" },
              {
                label: client.client_reference_number
                  ? `${client.name} (${client.client_reference_number})`
                  : client.name,
              },
            ]}
          />
          <div className="tab-content">
            <ClientDocuments
              clientID={client.contact_id}
              clientEmail={client.email_id}
              clientType={client.type}
              setShowClientIdSearch={noop}
              showUploads={null}
              typeOptions={[]}
              clientSubType={client.sub_type}
              clientPhone={(client.country_code || "") + (client.phone_number || "")}
              clientRecord={client}
            />
          </div>
        </div>
      </RolesContext.Provider>
    </AuthContext.Provider>
  );
};

export default EmbedClientDocuments;
